import { Button, LoadingSpinner } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import Grid from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import { LoadingSpinnerWithContent } from '../../components/LoadingSpinnerWithContent/LoadingSpinnerWithContent';
import { useBackBlocker } from '../../hooks/use-back-blocker';
import { useStore } from '../../hooks/use-store';
import { isDefined } from '../../utils/object-utils';
import './ManageSpecies.scss';

const ManageSpecies = observer(() => {
  const { manageSpeciesStore: store, globalModalStore, globalStore } = useStore();
  const navigate = useNavigate();
  const { loaders, colDefs, hasUnsavedChanges, singleItemEditMode, currentItemIndex } = store;

  useEffect(() => {
    void store.init();
  }, [store]);

  useBackBlocker(
    useCallback(
      allowBack => {
        if (hasUnsavedChanges || isDefined(currentItemIndex)) {
          globalModalStore.showLeavePagePopup(() => allowBack());
        } else {
          runInAction(() => (store.hasUnsavedChanges = false));
          allowBack();
        }
      },
      [currentItemIndex, globalModalStore, hasUnsavedChanges, store],
    ),
  );

  return (
    <div className='manage-species_wrapper'>
      <div className='spot-typography__heading--level-2'>Manage Species</div>
      <div className='spot-typography__text--tertiary'>
        Match your Cornerstone canine and feline species to Trupanion&apos;s species list.
        <br />
        This information is required to accurately assess eligibility for offers to your clients.
      </div>
      {loaders.page ? (
        <LoadingSpinner size='large' />
      ) : (
        <>
          {globalStore.practiceSpeciesBreeds.length === 0 ? (
            <Button
              className='retrieve-species-and-breeds-btn'
              variant='primary'
              size='small'
              disabled={loaders.retrieve}
              onClick={() => void store.retrieveSpeciesAndBreeds()}
              data-testid='retrieve-from-cornerstone-btn'
            >
              Retrieve From Cornerstone
            </Button>
          ) : (
            <Button
              className='retrieve-species-and-breeds-btn'
              iconLeft='refresh-redo'
              aria-label='refresh-redo'
              variant='link'
              size='small'
              disabled={loaders.retrieve}
              onClick={() => void store.retrieveSpeciesAndBreeds()}
              data-testid='refresh-from-cornerstone-btn'
            >
              Refresh From Cornerstone
            </Button>
          )}
          {loaders.retrieve && (
            <LoadingSpinnerWithContent size='large'>
              One moment while we retrieve your species and breeds information from your local
              Cornerstone Server.
            </LoadingSpinnerWithContent>
          )}
          {!loaders.retrieve && globalStore.practiceSpeciesBreeds.length > 0 && (
            <Grid
              id='species-management-grid'
              columns={colDefs}
              data={globalStore.practiceSpeciesBreeds}
            />
          )}
          <div className='page-bottom-actions'>
            <Button
              variant='link'
              size='small'
              className='back'
              onClick={() => void navigate(-1)}
              data-testid='back-btn'
            >
              Back
            </Button>
            <div>
              <Button
                variant='secondary'
                size='small'
                onClick={() => void navigate('/')}
                disabled={singleItemEditMode && isDefined(currentItemIndex)}
                data-testid='dashboard-btn'
              >
                Dashboard
              </Button>
              <Button
                variant='primary'
                size='small'
                disabled={
                  (singleItemEditMode ? isDefined(currentItemIndex) : !hasUnsavedChanges) ||
                  loaders.save
                }
                onClick={() => void store.saveAndRedirectToManageBreeds()}
                data-testid='manage-breeds-btn'
              >
                <Loader loading={loaders.save}>Manage Breeds</Loader>
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default ManageSpecies;
